import React, { useCallback, useEffect, useState } from "react";
import { X, RefreshCw, Download } from "lucide-react";
import apiHelper from "../apiHelper";
import downloadXlsx from "../utils/downloadXlsx";

// ERP-5249 — the banner under the Platform Pricing tabs after an Excel import is
// saved. Three states (Figma "Progress" / "All Success" / "With issues"):
//
//   pushing  amber   "Syncing prices to platforms" + per-platform done/total bars
//   done ok  green   "Pricing sync completed · N platform prices synced successfully ·
//                     N base prices updated to match WooCommerce"
//   done bad red     "Pricing sync completed with issues · Success / Failed / Pending" +
//                     per-platform Success/Failed/Pending, Try Again (attempts left), ✕
//
// State comes from GET sales/pricing/upload-batch/active/ (the newest saved batch
// not yet dismissed) and is polled every 3 s while pushing, so the banner survives
// navigation and refresh. Pending = Lazada campaign lock.

const POLL_MS = 3000;
const num = (n) => (n == null ? "0" : Number(n).toLocaleString("en-SG"));

// Text colours are the same in all three states (Figma): title #212529, secondary
// text #6C757D, counts carry their own colour (green / red / amber), dividers #ADB5BD.
const THEME = {
  pushing: { bg: "#FDF5E0", border: "#CC9A06", title: "#212529", bar: "#CC9A06", text: "#6C757D" },   // Figma "Progress": bg #FDF5E0, border warning-2 #CC9A06 (Jess, 16 Oct)
  ok: { bg: "#D1E7DD", border: "#146C43", title: "#212529", bar: "#198754", text: "#6C757D" },   // Figma "All Success": bg Green-100 #D1E7DD, border success-2 #146C43 (Jess, 16 Oct)
  bad: { bg: "#FFE9EC", border: "#F1AEB5", title: "#212529", bar: "#DC3545", text: "#6C757D" },   // Figma "With issues": bg #FFE9EC, border Red-200 #F1AEB5 (Jess, 16 Oct)
};
const GREEN = "#146C43", GREEN_NUM = "#198754", RED = "#B02A37", AMBER = "#CC9A06", GREY = "#6C757D", BLACK = "#212529", LINE = "#ADB5BD";

function Dot({ color }) {
  return <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 99, background: color, marginRight: 6 }} />;
}

function Bar({ done, total, color }) {
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 100;
  return (
    <span style={{ display: "inline-block", width: 70, height: 8, borderRadius: 99, background: "#DEE2E6", overflow: "hidden", verticalAlign: "middle" }}>
      <span style={{ display: "block", width: `${pct}%`, height: "100%", background: color, transition: "width .4s ease" }} />
    </span>
  );
}

export default function PricingSyncBanner() {
  const [batch, setBatch] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [reporting, setReporting] = useState(false);

  const load = useCallback(async () => {
    const res = await apiHelper("sales/pricing/upload-batch/active/", "GET");
    if (!res || !res.ok) return null;
    const data = await res.json().catch(() => null);
    setBatch(data && data.id ? data : null);
    return data;
  }, []);

  useEffect(() => { load(); }, [load]);

  const batchId = batch ? batch.id : null;
  const pushing = !!batch && (batch.status === "saved" || batch.status === "pushing");

  // Poll only while this tab is in front. A big batch pushes for tens of
  // minutes, and every tick is a GROUP BY over the batch's rows — a page left
  // open in a background tab used to keep firing it until the sync ended.
  // Coming back refreshes immediately, so the bars are never stale on screen.
  useEffect(() => {
    if (!pushing) return undefined;
    let timer = null;
    const start = () => { if (timer === null) timer = setInterval(load, POLL_MS); };
    const stop = () => { if (timer !== null) { clearInterval(timer); timer = null; } };
    const onVisibility = () => {
      if (document.hidden) { stop(); return; }
      load();
      start();
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, [batchId, pushing, load]);

  if (!batch) return null;

  const hasIssues = (batch.push_failed || 0) > 0 || (batch.push_pending || 0) > 0;
  const mode = pushing ? "pushing" : hasIssues ? "bad" : "ok";
  const t = THEME[mode];
  const platforms = batch.platforms || [];
  const canRetry = mode === "bad" && (batch.push_failed || 0) > 0 && (batch.retries_left || 0) > 0;

  const dismiss = async () => {
    setBatch(null);
    await apiHelper(`sales/pricing/upload-batch/${batch.id}/dismiss/`, "POST", {});
  };

  // Which rows failed. The banner only carries counts, and "20 Failed" is not
  // something anyone can act on — the report names every row and its outcome.
  const downloadReport = async () => {
    if (reporting) return;
    setReporting(true);
    try { await downloadXlsx(`sales/pricing/upload-batch/${batch.id}/report/`, "pricing-sync-report.xlsx"); }
    finally { setReporting(false); }
  };

  const retry = async () => {
    if (retrying || !canRetry) return;
    setRetrying(true);
    try {
      const res = await apiHelper(`sales/pricing/upload-batch/${batch.id}/retry/`, "POST", {});
      if (res && res.ok) {
        const data = await res.json().catch(() => null);
        if (data && data.id) setBatch(data);
      }
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div style={{ position: "relative", margin: "0 0 12px", padding: 15, borderRadius: 12, border: `1px solid ${t.border}`, background: t.bg, fontFamily: "Montserrat", fontSize: 12, color: t.text }}>
      {/* Headline row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          {mode === "pushing" ? (
            /* Figma "Progress": spinner on the left, title with the explanation on a second line under it */
            <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {/* 24px spinner, vertically centred against the two text lines (Figma) */}
              <span style={{ width: 24, height: 24, borderRadius: "50%", border: "3px solid #E9ECEF", borderTopColor: t.bar, animation: "spin 0.9s linear infinite", display: "inline-block", flexShrink: 0, boxSizing: "border-box" }} />
              <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontWeight: 700, fontSize: 12, color: t.title }}>Syncing prices to platforms</span>
                <span style={{ fontSize: 10, color: t.text }}>ERP platform prices have been updated. Syncing prices to platforms will take a few minutes.</span>
              </span>
            </span>
          ) : (
            <span style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, fontSize: 12, color: t.title }}>
              {mode === "ok" && <span style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${t.border}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: t.border }}>✓</span>}
              {mode === "bad" && <span style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${t.bar}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: t.bar }}>!</span>}   {/* icon stays red; the box border is the lighter Red-200 */}
              {mode === "ok" ? "Pricing sync completed" : "Pricing sync completed with issues"}
            </span>
          )}
          {mode === "ok" && (
            <>
              <span style={{ borderLeft: `1px solid ${LINE}`, paddingLeft: 14, color: GREY }}>
                <strong style={{ color: GREEN_NUM }}>{num(batch.push_success)}</strong> platform prices synced successfully
              </span>
              <span style={{ borderLeft: `1px solid ${LINE}`, paddingLeft: 14, color: GREY }}>
                <strong style={{ color: GREEN_NUM }}>{num(batch.woo_base_updated)}</strong> base prices updated to match WooCommerce
              </span>
            </>
          )}
          {mode === "bad" && (
            <>
              <span style={{ borderLeft: `1px solid ${LINE}`, paddingLeft: 14, display: "flex", gap: 12, fontWeight: 700, color: BLACK }}>
                <span><Dot color={GREEN} /><span style={{ color: GREEN }}>{num(batch.push_success)}</span> Success</span>
                <span><Dot color={RED} /><span style={{ color: RED }}>{num(batch.push_failed)}</span> Failed</span>
                <span><Dot color={AMBER} /><span style={{ color: AMBER }}>{num(batch.push_pending)}</span> Pending</span>
              </span>
              <span style={{ borderLeft: `1px solid ${LINE}`, paddingLeft: 14, color: GREY }}>
                <strong style={{ color: GREEN_NUM }}>{num(batch.woo_base_updated)}</strong> base prices updated to match WooCommerce
              </span>
            </>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexShrink: 0, paddingRight: 32 }}>  {/* 32px = the ✕ pinned to the banner corner below + its gap */}
          {!pushing && (
            <button type="button" onClick={downloadReport} disabled={reporting}
              style={{ display: "flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px", borderRadius: 6, border: "1px solid #212529", background: "#fff", color: "#212529", fontFamily: "Montserrat", fontSize: 11, fontWeight: 600, cursor: reporting ? "progress" : "pointer" }}>
              <Download size={12} /> {reporting ? "Preparing…" : "Report"}
            </button>
          )}
          {canRetry && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <button type="button" onClick={retry} disabled={retrying}
                style={{ display: "flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px", borderRadius: 6, border: "1px solid #212529", background: "#fff", color: "#212529", fontFamily: "Montserrat", fontSize: 11, fontWeight: 600, cursor: retrying ? "progress" : "pointer" }}>
                <RefreshCw size={12} /> {retrying ? "Retrying…" : "Try Again"}
              </button>
              <span style={{ fontSize: 10, fontWeight: 600, color: GREY }}>{batch.retries_left} attempt{batch.retries_left === 1 ? "" : "s"} remaining</span>
            </div>
          )}
          {!pushing && (
            <button type="button" onClick={dismiss} aria-label="Dismiss" style={{ position: "absolute", top: 15, right: 15, background: "none", border: "none", cursor: "pointer", padding: 0, height: 28, display: "flex", alignItems: "center", color: BLACK }}><X size={20} strokeWidth={2.5} /></button>
          )}
        </div>
      </div>

      {/* Per-platform row — Figma draws a full-width hairline between the headline and the platform bars in all three states */}
      {platforms.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 0, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${LINE}`, flexWrap: "wrap", color: GREY }}>
          {platforms.map((p, i) => (
            <span key={p.platform} style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 16, marginRight: 16, borderRight: i < platforms.length - 1 ? `1px solid ${LINE}` : "none" }}>
              <strong style={{ color: BLACK }}>{p.platform}</strong>
              {mode === "bad" ? (
                <>
                  <span><Dot color={GREEN} /><span style={{ color: GREEN }}>{num(p.success)}</span> Success</span>
                  {p.failed > 0 && <span><Dot color={RED} /><span style={{ color: RED }}>{num(p.failed)}</span> Failed</span>}
                  {p.pending > 0 && <span><Dot color={AMBER} /><span style={{ color: AMBER }}>{num(p.pending)}</span> Pending</span>}
                </>
              ) : (
                <>
                  <span><strong style={{ color: t.bar }}>{num(p.done)}</strong> / {num(p.total)}</span>
                  <Bar done={p.done} total={p.total} color={t.bar} />
                </>
              )}
            </span>
          ))}
        </div>
      )}
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
}
